import {
  Card,
  CheckIcon,
  Flex,
  Group,
  Table,
  Text,
  UnstyledButton,
  rem,
} from '@mantine/core';
import { useAttachmentsFormContext } from '../ClientDetailsSection';
import { useContext, useRef, useState } from 'react';
import { modals } from '@mantine/modals';
import { deleteFetcher, postFetcher } from '@/api/fetchers';
import { useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { ServiceContext } from '@/layouts/Person/PersonLayout';
import { dateFormat } from '@/index';
import dayjs from 'dayjs';
import { type ClientAttachment } from '@/schemas/API/GET/client/attachment/response';
import { displayError } from '@/utils/error/errorHandler';
import { subPath } from '@/subpath.config';
import { downloadFileWithBearerToken } from '@/utils/functions';

export const AttachmentCard: React.FC = () => {
  const form = useAttachmentsFormContext();
  const [fileType] = useState('vse');
  const { clientId } = useParams<{ clientId: string }>();
  const { service } = useContext(ServiceContext);

  const handleDelete = async (removeId: number): Promise<void> => {
    let index = -1;
    for (let i = 0; i < Object.keys(form.values.attachments).length; i++)
      if (form.values.attachments[i].id === removeId) {
        index = i;
        break;
      }
    if (index < 0) return;

    try {
      await deleteFetcher(`/clients/${clientId}/attachments/${removeId}`);
      form.removeListItem('attachments', index);

      notifications.show({
        message: 'Příloha byla úspěšně smazána.',
        color: 'green',
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
    } catch (err) {
      displayError(err);
    }
  };

  function showConfirmModal(id: number): void {
    modals.openConfirmModal({
      title: 'Smazat přílohu',
      centered: true,
      children: <div>Určitě chcete přílohu smazat? Tato akce je nevratná.</div>,
      labels: { confirm: 'Smazat', cancel: 'Zrušit' },
      confirmProps: { color: 'red' },
      onCancel: () => {},
      onConfirm: () => {
        void handleDelete(id);
      },
    });
  }

  function formatDate(inputDate: string): string {
    const parsedDate = new Date(inputDate);
    const day = parsedDate.getUTCDate().toString().padStart(2, '0');
    const month = (parsedDate.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = parsedDate.getUTCFullYear();
    return `${day}.${month}.${year}`;
  }

  function showTable(): JSX.Element {
    const attachments: ClientAttachment[] = [];
    for (let i = 0; i < Object.keys(form.values.attachments).length; i++)
      if (
        (fileType === 'vse' || form.values.attachments[i].type === fileType) &&
        form.values.attachments[i].serviceId === service?.id
      )
        attachments.push(form.values.attachments[i]);

    attachments.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

    const downloadSvgPath = `${subPath}/icons/download.svg`;
    const removeSvgPath = `${subPath}/icons/remove.svg`;
    return (
      <Table
        verticalSpacing="md"
        style={{
          fontSize: 'medium',
        }}
      >
        <Table.Tbody>
          {attachments.map(attachment => (
            <Table.Tr key={attachment.id}>
              <Table.Td className="text-left">{attachment.name}</Table.Td>
              <Table.Td className="text-right">
                <Flex gap="md" justify="flex-end">
                  {formatDate(attachment.createdAt)}
                  <UnstyledButton
                    style={{ backgroundColor: 'transparent' }}
                    onClick={() => {
                      downloadFileWithBearerToken(attachment.url);
                    }}
                  >
                    <img src={downloadSvgPath} alt="download icon"></img>
                  </UnstyledButton>
                  <UnstyledButton
                    style={{ backgroundColor: 'transparent' }}
                    onClick={() => {
                      showConfirmModal(attachment.id);
                    }}
                  >
                    <img src={removeSvgPath} alt="trash icon"></img>
                  </UnstyledButton>
                </Flex>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  }

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.target.files?.[0];
    if (file) {
      void handleFilePost(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFilePost = async (file: File): Promise<void> => {
    try {
      const formData = new FormData();
      formData.append('Content-Type', 'multipart/form-data');
      formData.append('name', file.name);
      formData.append('expirationDate', dayjs().format(dateFormat));
      formData.append('signatureDate', dayjs().format(dateFormat));
      formData.append('serviceId', `${service?.id}`);
      formData.append('file', file);
      formData.append('type', 'OTHER');
      formData.append('Accept', 'application/json');

      const newAttachment = await postFetcher(
        `/clients/${clientId}/attachments`,
        formData
      );
      if (
        form.values.attachments.map(attachment => {
          return attachment.id !== newAttachment.id;
        })
      )
        form.insertListItem('attachments', newAttachment);

      notifications.show({
        message: 'Příloha byla úspěšně nahrána.',
        color: 'green',
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
    } catch (err) {
      displayError(err);
    }
  };

  const addSvgPath = `${subPath}/icons/add.svg`;
  return (
    <Card shadow="sm" padding="md" radius="md" withBorder>
      <Group justify="space-between">
        <Group>
          <Text fw={700} size="xl" className="ml-2">
            Přílohy
          </Text>
        </Group>
        <UnstyledButton
          onClick={handleFileUpload}
          style={{ backgroundColor: 'transparent' }}
        >
          <img src={addSvgPath} alt="add icon"></img>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            data-testid="file-upload-input"
          />
        </UnstyledButton>
      </Group>
      {showTable()}
    </Card>
  );
};
